import React, { CSSProperties, useCallback, useMemo, useState } from 'react';
import { ResourceStrings } from '../../../ResourceStrings';
import styled from 'styled-components/macro';
import { Icon } from '@fluentui/react/lib/Icon';
import { PatchType, NotificationInfo } from '../../../store/appState';
import { App, SignInState } from '../../../app';
import { IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import { forMinWidth } from '../../../theme/breakpoints';
import TagsEditor from './TagsEditor';
import Button from '../../shared/Button';
import Flex from '../../shared/Flex';
import * as Actions from '../../../store/actions';
import { hasPermission } from '../../../config/accessPolicies';
import { Permission, UserRole } from '../../../config/permissions';
import { MessageBar, MessageBarType } from '@fluentui/react/lib/MessageBar';
import ShareLinkDialog from '../../shared/ShareLinkDialog';
import { ticketNotificationKey } from '../../../store/actionsHandler';
import { ticketReadonlyThresholdDays } from '../../../constants/config';
import { keepFormOpen, ticketOperationKey, UIOperationState, useOperation } from '../../../store/operation';
import { ensureSignedIn } from '../../../utils/webPage';
import { problemStates } from '../../../config/problemStates';
import { UserProperties } from '../../../UserProperties';
import { VoteType } from '../../../store/actions';
import { ideaStates } from '../../../config/ideaStates';
import { Link } from 'react-router-dom';
import { IdeaStateConfig, ProblemStateConfig } from '../../../config/feedbackStateConfig';
import NotificationDialog from '../../shared/NotificationDialog';
import { hasItems } from '../../../utils/collections';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/rootState';
import OperationNotification from './OperationNotification';
import { IdeaStates, ProblemStates } from '../../../constants/feedback';
import { PostType, ReactionType, RootPost, Tag, TagCategory } from '../../../RootPost';
import HiddenTextInfo from './HiddenTextInfo';
import HtmlPreview from '../../shared/HtmlPreview';
import Avatar from '../../shared/Avatar';
import { CommandBar, ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import { IButtonProps } from '@fluentui/react/lib/Button';
import { TelemetryResult } from '../../../telemetry/TelemetryHelper';

const fixedInTagPrefix: string = "fixed in:";

const tagsCompare = (a: Tag, b: Tag): number => {
    // fixed in tags should appear at the begining
    if (a.value.toLowerCase().startsWith(fixedInTagPrefix) && !b.value.toLowerCase().startsWith(fixedInTagPrefix)) return -1;
    return 0;
}

export interface ContextualMenuItem extends IContextualMenuItem {
    data?: {
        requiredPermission?: Permission;
    };
}

export interface RichTicketDescriptionProps {
    position?: number
    signInState?: SignInState;
    userRoles: UserRole[];
    ticket: RootPost;
    commentCount?: number;
    isFollowing?: boolean;
    hideVotes?: boolean;
    isTicketReadOnly?: boolean;
    lastStateChangeDaysAgo?: number;
    userProperties: UserProperties;
    showNeedMoreInfoLink: boolean;
    solutionElementId?: string;   // DOM Id of the solution element
    mostRelevantFixedInRelease?: string;
    forNMIAction?: boolean;

    showMinimalView?: boolean;  // Don't show menu, editing options etc. when true. Used for search results page
    fillMissingInfoClicked: () => void;
}

export default function RichTicketDescription(props: RichTicketDescriptionProps): JSX.Element {
    const {
        position, ticket, signInState, userRoles, isFollowing, hideVotes, isTicketReadOnly, lastStateChangeDaysAgo,
        mostRelevantFixedInRelease, fillMissingInfoClicked, showNeedMoreInfoLink, solutionElementId
    } = props;

    const [showLinkDialog, setShowLinkDialog] = useState(false);
    const [showInfoDialog, setShowInfoDialog] = useState(false);

    const editTicketOpKey = ticketOperationKey(ticket?.id, PatchType.ModifyTicket);
    const editTicketOperation = useOperation(editTicketOpKey);

    let stateConfig = ticket.type === PostType.suggestion ? ideaStates[ticket.state] : problemStates[ticket.state];
    if (stateConfig == null) {
        console.error("ERR_UNKNOWN_STATE: " + ticket.state);
        stateConfig = ticket.type === PostType.suggestion ? ideaStates[IdeaStates.invalid] : problemStates[ProblemStates.invalid];
    }
    let stateBgColor = stateConfig.backgroundColor;
    let stateTextColor = stateConfig.textColor;

    const isDuplicateState = ticket.state === ProblemStates.duplicate || ticket.state === IdeaStates.duplicate;
    if (isDuplicateState && mostRelevantFixedInRelease) {
        // Dupe of a closed-fixed ticket. For now it relies on manually added fixed-in tags (to closed-duplicate tickets) to figure if actual ticket is fixed or not
        // Could be automated in future if we have better ways to link dupe and original tickets
        const fixedStateConfig = ticket.type === PostType.suggestion ? ideaStates[IdeaStates.fixed] : problemStates[ProblemStates.fixed];
        stateBgColor = fixedStateConfig.backgroundColor;
        stateTextColor = fixedStateConfig.textColor;
    }

    const contextualNotifications = useSelector((state: RootState) => state.appState.contextualNotifications[ticketNotificationKey(ticket?.id)])
    const criticalNotifications = contextualNotifications?.filter(x => x?.title && x?.isCritical);

    const content = <>
        <TicketHeader
            ticket={ticket}
            stateConfig={stateConfig}
            fillMissingInfoClicked={fillMissingInfoClicked}
            showMinimalView={props.showMinimalView}
            isFollowing={isFollowing}
            signInState={signInState}
            showLinkDialog={() => setShowLinkDialog(true)}
            userRoles={userRoles}
            showNeedMoreInfoLink={showNeedMoreInfoLink}
            solutionElementId={solutionElementId}
        />
        <TicketDescriptionBody
            position={position}
            ticket={ticket}
            signInState={signInState}
            userRoles={userRoles}
            hideVotes={hideVotes}
            isTicketReadOnly={isTicketReadOnly}
            lastStateChangeDaysAgo={lastStateChangeDaysAgo}
            showMinimalView={props.showMinimalView}
        />
    </>;

    return <PostDetailWrapper>
        {!ticket && <div>Invalid feedback item.</div>}

        {ticket && !keepFormOpen(editTicketOperation.operation) && <MainContent>
            <div style={{
                "--state-color": stateBgColor,
                "--state-contrast-color": stateTextColor,
                "--ticket-title-bg-color": stateBgColor,
                "--ticket-title-text-color": stateTextColor,
                "--state-bg-color": stateTextColor,
                "--state-text-color": stateBgColor,
            } as CSSProperties}>
                <div css={"border: 0.1rem solid gray;"}>
                    {content}
                </div>
            </div>
        </MainContent>
        }

        <ShareLinkDialog
            title="Share a link to this feedback"
            message="Copy and paste the URL below to share a direct link to this feedback."
            url={`${window.location.origin}${window.location.pathname}`}
            isOpen={showLinkDialog}
            onDismiss={() => setShowLinkDialog(false)} />

        <NotificationDialog
            notificationType="information"
            isOpen={hasItems(criticalNotifications)}
            message={criticalNotifications?.[0]?.title}
            canDismiss
            onDismiss={() => Actions.clearContextualNotification(ticketNotificationKey(ticket?.id))}
        />
        <NotificationDialog
            notificationType="information"
            isOpen={showInfoDialog}
            message={stateConfig?.description}
            canDismiss
            onDismiss={() => setShowInfoDialog(false)}
        />
    </PostDetailWrapper >
}

interface TicketHeaderProps {
    ticket: RootPost;
    stateConfig: IdeaStateConfig | ProblemStateConfig;
    fillMissingInfoClicked: () => void;
    showMinimalView?: boolean;
    isFollowing?: boolean;
    signInState?: SignInState;
    showLinkDialog: () => void;
    userRoles: UserRole[];
    showNeedMoreInfoLink: boolean;
    solutionElementId?: string;   // DOM Id of the solution element
}

const TicketHeader = (props: TicketHeaderProps): JSX.Element => {
    const { ticket, stateConfig, fillMissingInfoClicked, showMinimalView, isFollowing, signInState, showLinkDialog,
        userRoles, showNeedMoreInfoLink, solutionElementId } = props;
    const isUserBanned = useSelector((state: RootState) => state.appState.isUserBanned);
    const isInReadOnlyMode = useSelector((state: RootState) => state.appState.isInReadOnlyMode);

    const solutionCountIconTitle: string = ticket.type === PostType.problem ? `${ticket.solutionCount} ${ResourceStrings.answers()}` : undefined;

        const showSolutionClicked = (): void => {
        const el = document.getElementById(props.solutionElementId);
        if (el) {
            el.scrollIntoView();
        }
    }

    const ticketOpKey = ticketOperationKey(ticket?.id);
    const ticketOperation = useOperation(ticketOpKey);

    const editTicketOpKey = ticketOperationKey(ticket?.id, PatchType.ModifyTicket);
    const editTicketOperation = useOperation(editTicketOpKey);

    const editTicketTagsOpKey = ticketOperationKey(ticket?.id, PatchType.ModifyTicketTags);
    const editTicketTagsOperation = useOperation(editTicketTagsOpKey);

    // We usually won't have more than one operation in progress for ticket so just track that for follow
    const isFollowOpInProgress = ticketOperation.operation?.state === UIOperationState.InProgress;

    const contextSpecificRoles = useMemo(
        () => ticket?.iCreatedThis ? [UserRole.Author] : []
        , [ticket]
    );

    const allAvailableRoles = useMemo(
        () => [...userRoles, ...contextSpecificRoles]
        , [userRoles, contextSpecificRoles]
    );

    const onFollowClick = useCallback((id: number, isFollowing: boolean, signInState: SignInState): void => {
        if (ensureSignedIn(
            signInState,
            ticketNotificationKey(ticket?.id),
            `Please sign in to ${isFollowing ? "stop following" : "follow"} this feedback.`)
        ) {
            if (isFollowing) {
                ticketOperation.setState(UIOperationState.InProgress);
                Actions.unFollowTicket(id, ticketOpKey);
            } else {
                ticketOperation.setState(UIOperationState.InProgress);
                Actions.followTicket(id, ticketOpKey);
            }
        }
    }, [ticket, ticketOpKey, ticketOperation]);


    const followMenuLabel = (isFollowing: boolean): string => {
        return isFollowing ? "Unfollow" : "Follow";
    }

    const overflowMenuProps: IButtonProps = {
        ariaLabel: "Show Options"
    };

    const ticketMenuProps = useCallback(
        (ticket: RootPost, isFollowing: boolean, isFollowOpInProgress: boolean, signInState: SignInState): ICommandBarItemProps[] => [
            {
                key: "Edit",
                text: "Edit",
                disabled: isUserBanned || isInReadOnlyMode,
                iconProps: { iconName: 'SingleColumnEdit' },
                data: {
                    requiredPermission: Permission.Ticket_Edit,
                },
                onClick: () => {
                    editTicketOperation.resetOperation();
                    editTicketTagsOperation.resetOperation();
                    editTicketOperation.setState(UIOperationState.InProgress);
                }
            },
            {
                key: "follow",
                text: followMenuLabel(isFollowing),
                disabled: isFollowOpInProgress || isInReadOnlyMode,
                data: {
                    requiredPermission: Permission.Ticket_FollowOrUnfollow,
                },
                onClick: () => {
                    onFollowClick(ticket.id, isFollowing, signInState);
                }
            },
            {
                key: "share",
                text: "Share",
                onClick: () => {
                    showLinkDialog();
                }
            },
            {
                key: "flagForModeration",
                text: "Report content",
                disabled: isInReadOnlyMode,
                data: {
                    requiredPermission: Permission.Ticket_FlagForModeration,
                },
                onClick: () => {
                    ticketOperation.setState(UIOperationState.InProgress);
                    Actions.flagForModeration(ticket.id, ticket.id, ticketOpKey);
                }
            },
        ]
        , [editTicketOperation, editTicketTagsOperation, onFollowClick, ticketOpKey, ticketOperation]
    );

    const ticketMenuItems = useMemo(
        () => ticket && ticketMenuProps(ticket, isFollowing, isFollowOpInProgress, signInState)
            .filter(x => hasPermission(x.data?.requiredPermission, allAvailableRoles))
        , [ticket, allAvailableRoles, ticketMenuProps, isFollowing, isFollowOpInProgress, signInState]
    );

    return <>
        <TopBar
            aria-label={ticket.type === PostType.suggestion ? "Type:Suggestion, Title:" + App.EscapeHtmlRawDisplay(ticket.title) : "Type:Problem, Title:" + App.EscapeHtmlRawDisplay(ticket.title)}
            style={{
                "--ticket-title-bg-color": "var(--color-surface)",
                "--ticket-title-text-color": "var(--color-on-surface)",
                "--state-bg-color": "var(--state-color)",
                "--state-text-color": "var(--state-contrast-color)",
                fontSize: "1.8rem",
            } as CSSProperties}
        >
            {(<>
                <h2
                    tabIndex={0}
                    css="word-break: break-word; display: flex; align-items: center;"
                >
                    <IconCircle $isSuggestion={ticket.type === PostType.suggestion}>
                        <Icon iconName={ticket.type === PostType.suggestion ? "Lightbulb" : "Bug"} style={{ backgroundColor: 'transparent' }} />
                    </IconCircle>
                    <span aria-hidden="true"
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{ __html: App.EscapeHtmlRawDisplay(ticket.title) }}
                    />
                </h2>

                <span className="right-icons">
                    <span tabIndex={0} className={`state-label ${ticket.state}`}>{stateConfig.label}</span>

                    {showNeedMoreInfoLink && !isUserBanned && !isInReadOnlyMode && <span>
                        <Button
                            buttonType="normal"
                            size="compact"
                            className="top-bar-action-btn"
                            onClick={fillMissingInfoClicked}
                        >
                            {ResourceStrings.provideMoreInfo()}
                        </Button>
                    </span>}

                    {stateConfig?.isFinalState && solutionElementId && <span>
                        <Button
                            buttonType="normal"
                            size="compact"
                            className="top-bar-action-btn"
                            onClick={showSolutionClicked}
                        >
                            {stateConfig?.isFixedState ? "View solution" : "View resolution"}
                        </Button>
                    </span>}

                    {ticket.type === PostType.problem && <span
                        tabIndex={0}
                        title={solutionCountIconTitle}
                        aria-label={solutionCountIconTitle}
                        className="metric-container"
                    >
                        <>
                            <span aria-label={ticket.solutionCount + ticket.solutionCount === 1 ? "solution" : "solutions"} className="metric">{ticket.solutionCount}</span>
                            <Icon iconName="SkypeCircleCheck" />
                        </>
                    </span>}

                    <span
                    tabIndex={0}
                        aria-label={`${ticket.commentCount} ${ResourceStrings.comments()}`}
                        title={`${ticket.commentCount} ${ResourceStrings.comments()}`}
                        className="metric-container"
                    >
                        <span className="metric">{ticket.commentCount}</span>
                        <svg className="comment-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path d="M5.25 3C3.45507 3 2 4.45507 2 6.25V14.75C2 16.5449 3.45508 18 5.25 18H6V20.75C6 21.7801 7.17595 22.368 8 21.75L13 18H18.75C20.5449 18 22 16.5449 22 14.75V6.25C22 4.45507 20.5449 3 18.75 3H5.25Z"/>
                        </svg>
                    </span>
                    <span>
                        {!showMinimalView && ticketMenuItems && ticketMenuItems.length !== 0 &&
                            <CommandBar items={[]} overflowItems={ticketMenuItems} overflowButtonProps={overflowMenuProps} />
                     }
                    </span>
                </span>
            </>)}
        </TopBar>
    </>
}

interface TicketDescriptionBodyProps {
    position: number;
    signInState?: SignInState;
    userRoles: UserRole[];
    ticket: RootPost;
    hideVotes?: boolean;
    isTicketReadOnly?: boolean;
    lastStateChangeDaysAgo?: number;
    showMinimalView?: boolean;
}

const TicketDescriptionBody = (props: TicketDescriptionBodyProps): JSX.Element => {
    const { ticket, signInState, userRoles, hideVotes, isTicketReadOnly, lastStateChangeDaysAgo, showMinimalView } = props;

    const userId = useSelector((state: RootState) => state.appState.userVsid);
    const isInReadOnlyMode = useSelector((state: RootState) => state.appState.isInReadOnlyMode);

    const isUserBanned = useSelector((state: RootState) => state.appState.isUserBanned);

    const ticketOpKey = ticketOperationKey(ticket?.id);
    const ticketOperation = useOperation(ticketOpKey);

    const editTicketOpKey = ticketOperationKey(ticket?.id, PatchType.ModifyTicket);
    const editTicketOperation = useOperation(editTicketOpKey);

    const editTicketTagsOpKey = ticketOperationKey(ticket?.id, PatchType.ModifyTicketTags);
    const editTicketTagsOperation = useOperation(editTicketTagsOpKey);

    let reportDate = "";
    if (ticket?.createdDate) {
        reportDate = App.ParseDateTimeToLocal(ticket.createdDate);
    }

    const reasonCantVote = (ticket: RootPost, signInState: SignInState, cancelVote: boolean): string => {
        console.log("signInState");
        console.log(signInState);
        console.log(ticket);
        if (ticket.iCreatedThis) {
            return ResourceStrings.youCreatedIt();
        }

        if (ticket.hasVoted && !cancelVote) {
            return ResourceStrings.alreadyVoted();
        }

        if (signInState !== SignInState.Success) {
            return ResourceStrings.authNeeded_Vote();
        }

        if (ticket.cantVote) {
            return ResourceStrings.cantVote();
        }

        return null;
    }

    const onVoteClick = (ticket: RootPost, signInState: SignInState, cancelVote: boolean, voteType: ReactionType): void => {
        const reason = reasonCantVote(ticket, signInState, cancelVote);
        if (reason) {
            Actions.setContextualNotification(ticketNotificationKey(ticket?.id), {
                title: reason,
                type: "information",
                isCritical: true
            } as NotificationInfo)
        } else {
            if (cancelVote) {
                ticketOperation.setState(UIOperationState.InProgress);
                Actions.voteOnNode(ticket.id, ticket.id, ticketOpKey, voteType, true);
            } else {
                ticketOperation.setState(UIOperationState.InProgress);
                Actions.voteFor(ticket, false, "", ticketOpKey);
            }
        }
    }

    const contextSpecificRoles = useMemo(
        () => ticket?.iCreatedThis ? [UserRole.Author] : []
        , [ticket]
    );

    const allAvailableRoles = useMemo(
        () => [...userRoles, ...contextSpecificRoles]
        , [userRoles, contextSpecificRoles]
    );

    const permissions = useMemo(
        () => ({
            ticketEdit: hasPermission(Permission.Ticket_Edit, allAvailableRoles),
            viewInternalTicket: hasPermission(Permission.Ticket_ViewInternalTicket, allAvailableRoles)
        })
        , [allAvailableRoles]
    );

    const showTagEditorClicked = (): void => {
        editTicketOperation.resetOperation();
        editTicketTagsOperation.resetOperation();

        editTicketTagsOperation.setState(UIOperationState.InProgress);
    }

    const handleSaveTags = (addedTags: string[], removedTags: string[]): void => {
        Actions.updateTicket(ticket.id, editTicketTagsOpKey, null, null, addedTags, removedTags);
    }

    const filteredSortedTags = useMemo(() => {
        if (!ticket?.tags) {
            return [];
        }

        return ticket.tags.filter(tag => tag.category === TagCategory.generic || tag.category === TagCategory.space).sort(tagsCompare);
    }, [ticket]);

    // Fixed Bug 1533813: Ensure narrator is reading the text fully in Edge Chromium
    const getClosedTicketWarning = (ticket: RootPost): string => {
        return `This issue is read only, because it has been in the ${App.LocalizeStateName(ticket.state, ticket.type)} state for over ${ticketReadonlyThresholdDays} days. It was closed for ${lastStateChangeDaysAgo} days.`;
    };

    return <>
        <div className="ticket-panel">
            <VotesSection>
                <VoteButtonWrapper>
                    <span className="vote-buttons">
                        <Button
                            buttonType="link"
                            size="compact"
                            aria-label="Vote up"
                            disabled={isInReadOnlyMode}
                            onClick={() => onVoteClick(ticket, signInState, false, ReactionType.upvote)}
                        >
                            <Icon iconName="ChevronUpSmall" style={ticket.hasVoted ? {color: "var(--color-on-surface-light)"} : {}} />
                        </Button>
                        <Button
                            buttonType="link"
                            size="compact"
                            aria-label="Cancel vote"
                            disabled={!ticket.hasVoted || isInReadOnlyMode}
                            onClick={() => onVoteClick(ticket, signInState, true, ReactionType.downvote)}
                        >
                            <Icon iconName="ChevronDownSmall" style={ticket.hasVoted ? {} : { color: "var(--color-on-surface-light)" }} />
                        </Button>
                    </span>
                    {!hideVotes && <span className="vote-count-wrapper">
                        <span className="vote-count" aria-label={`${ticket.totalVotes} votes`}>{ticket.totalVotes}</span>
                        <span className="vote-label">Votes</span>
                    </span>}
                </VoteButtonWrapper>
            </VotesSection>

            <section>
                {props.showMinimalView && <OperationNotificationWrapper
                    operation={ticketOperation.operation}
                    resetOperation={ticketOperation.resetOperation}
                    className="top-level-text"
                />}

                <div className={showMinimalView ? "ticket-description minimal-view" : "ticket-description"}>
                    <AuthorBar>
                        <Flex alignItems="center">
                            <Avatar className='avatar-logo' name={ticket.author.name} />

                            <Author href={ticket.createdByUrl} target="_blank" rel="noopener noreferrer">{ticket.author.name}</Author>
                        </Flex>
                        <span className="dash-separator"> - </span>
                        <ReportDate>Reported {reportDate}</ReportDate>

                        {permissions.viewInternalTicket && <>
                            {ticket.intUrl && <a
                                href={ticket.intUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="open-internal-ticket"
                                aria-label="View internal ticket"
                            >
                                <span>View internal ticket</span>
                                <Icon iconName="OpenInNewWindow" />
                            </a>}
                            {ticket.intUrl == null && <span className="open-internal-ticket">
                                <Icon iconName="Warning" />
                                <span>No internal ticket yet</span>
                            </span>}
                        </>}
                    </AuthorBar>

                    <HiddenTextInfo post={ticket} currentUserId={ticket.currentUserId} />
                    <Description>
                        <HtmlPreview text={ticket.text} isPublic={ticket.isPublic} />
                    </Description>
                </div>
            </section>
        </div>
        <TagsBar>
            {!keepFormOpen(editTicketTagsOperation.operation) && (
                <TagsContainer alignLeft={false}>
                    {filteredSortedTags.map((tag, i) => (
                        <TagItem
                            key={i}
                            className={tag.value?.toLowerCase()?.startsWith(fixedInTagPrefix) && "fixedin"}
                        >
                            {tag.value}
                        </TagItem>
                    ))}
                    {!showMinimalView && !isInReadOnlyMode && permissions.ticketEdit && !isUserBanned && <Button
                        size="compact"
                        buttonType="link"
                        aria-label="Edit tags"
                        onClick={() => showTagEditorClicked()}
                    >
                        <Icon iconName="Edit" />
                    </Button>}
                </TagsContainer>
            )}
            {showMinimalView && (
                    <Link
                        to={App.getTicketPath(ticket.id, ticket.title, false, true, userId)}
                    onClick={(evt) => {
                        App.telemetry.postUserTask(
                            "searchResultClicked", TelemetryResult.Success, "", "", {
                                "position": (props.position + 1).toString(),
                                "state": ticket.state,
                                "trackingId": ticket.trackingId ?? "",
                                "title": ticket.title ?? "",
                                "solution_count": ticket.solutionCount?.toString() ?? "",
                                "comment_count": ticket.commentCount?.toString() ?? "",
                            }
                        );
                        evt.stopPropagation();
                    }}
                    >
                        Go to details and discussion &gt;
                    </Link>
            )}
            {keepFormOpen(editTicketTagsOperation.operation) && <TagsEditor
                tags={ticket.tags}
                onCancel={() => editTicketTagsOperation.resetOperation()}
                onSave={handleSaveTags}
            />}
        </TagsBar>

        {!showMinimalView && isTicketReadOnly &&
            <NotificationBar messageBarType={MessageBarType.warning}>
                {getClosedTicketWarning(ticket)}
            </NotificationBar>
        }
    </>
}

const TopBar = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: .7rem 2rem .7rem 2rem;
    align-items: center;

    &, i, span, div {
        background-color: var(--ticket-title-bg-color);
        color: var(--ticket-title-text-color);
        font-weight: bold;
    }

    *:focus {
        outline-color: var(--ticket-title-text-color);
    }

    strong {
        font-weight: 600;
    }

    && button, && button:hover {
        align-self: center;
        font-size: 1.6rem;
        font-weight: normal;
        color: white;
        border-color: white;
        background-color: transparent;
        padding: .1rem 1rem;
    }

    && .btn-link, && .btn-link:hover {
        color: var(--color-on-surface-link);
        font-weight: 600;
    }

    .top-bar-state {
        strong {
            font-size: 2.4rem;
        }

        i {
            font-size: 1.4rem;
            margin-top: .6em;
            margin-bottom: .3em;
        }
    }

    button.top-bar-action-btn {
        border-radius: .2rem;
        padding: .1rem 0;
        text-transform: capitalize;
        font-size: 1.2rem;
        min-width: 15rem;
        text-align: center;

        color: var(--state-bg-color);
        border-color: var(--state-bg-color);
        justify-content: center;
        font-weight: bold;
    }

    button:hover.top-bar-action-btn {
        border-radius: .2rem;
        padding: .1rem 0;
        text-transform: capitalize;
        font-size: 1.2rem;
        min-width: 15rem;
        text-align: center;

        color: var(--state-bg-color);
        border-color: var(--state-bg-color);
        justify-content: center;
        font-weight: bold;
        border-style: inset
    }

    .top-bar-action-btn-info {
        border: none;
    }

    &.ticket-header-default .right-icons {
        text-align: left;
    }

    .right-icons {
        display: flex;
        white-space: nowrap;
        align-items: center;
        margin-top: 0.6rem;
        text-align: right;


        >* + * {
            margin-left: 1rem;
        }

        .vote-icon {
            transform: rotate(90deg);
            transform-origin: center;
        }

        .metric {
            margin-right: .5rem;
        }

        .metric, i {
            vertical-align: middle;
            color: var(--state-bg-color);
        }

        .metric-container {
            min-width: 3rem;
        }

        .state-label {
            border-radius: .2rem;
            padding: .3rem 0;
            text-transform: capitalize;
            font-size: 1.2rem;
            min-width: 15rem;
            text-align: center;

            background-color: var(--state-bg-color);
            color: var(--state-text-color);
        }

        .comment-icon {
            vertical-align: middle;
            fill: var(--state-bg-color);
            height: 22px;
            width: 22px;
        }
    }

    ${forMinWidth.md`
        flex-direction: row;

        >* + * {
            margin-left: 3rem;
        }

        .right-icons {
            margin-top: 0;

            .metric-container {
                text-align: right;
            }
        }
    `}
`;

const MainContent = styled.div`
    .open-internal-ticket {
        display: flex;
        align-items: center;
        font-size: 1.4rem;

        >* + * {
            margin-left: .6rem;
        }
    }

    ${forMinWidth.md`
        .header {
            ${TopBar} {
                padding-left: 1rem;
            }
        }
    `}
`;

const PostDetailWrapper = styled.div`
    padding-bottom: 1rem;

    .ticket-panel {
        display: grid;
        position: relative;
        grid-template-columns: minmax(0, 1fr);
        grid-template-areas: "vote"
                                "main-content";
        grid-gap: .5rem;
        justify-items: flex-start;
        padding: 2rem 2rem 0;

        .ms-MessageBar-innerText > span {
            font-size: 1.4rem;
            line-height: 140%;
        }

        ${forMinWidth.md`
            grid-template-columns: min-content minmax(0, 1fr);
            grid-template-areas: "vote main-content";
            justify-items: normal;
        `}
    }

    .ticket-panel > section {
        grid-area: main-content;
        padding: 0;
        width: 100%;

        ${forMinWidth.md`
            padding: 0 0 0 3rem;
        `}
    }

    .ticket-panel .ticket-description {
        ${forMinWidth.md`
            padding-right: 4rem;
        `}
    }

    .minimal-view {
        overflow: hidden;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
    }

    button {
        white-space: nowrap;
    }

    .message-light {
        color: var(--color-on-surface-light);
    }

    .post-state {
        padding: 0 .5rem;
    }

    .view-feedback-footer {
        margin: 2rem;
        font-size: 1.8rem;
        font-weight: 600;
        position: relative;
        text-align: right;
    }      
`;

const VotesSection = styled.div`
    grid-area: vote;
    display: flex;
    justify-content: flex-start;
    flex-direction: column;
    align-items: center;
    margin-bottom: .8rem;
`;

const VoteButtonWrapper = styled.span`
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 50%;
    height: 5rem;
    margin-top: 0.2rem;

    .vote-buttons {
        display: flex;
        flex-direction: column;
        align-items: center;
        margin-right: .8rem;
        margin-top: -3px;
    }

    button + button {
        margin-top: 0.8rem;
    }

    button i {
        color: var(--color-on-surface-link);
        font-size: 1.2rem;
    }

    .vote-count-wrapper {
        display: flex;
        flex-direction: column;
        align-items: center;

        .vote-count {
            font-size: 2.4rem;
            font-weight: 600;
            color: var(--color-on-surface-light);
        }

        .vote-label {
            font-size: 1.4rem;
            color: var(--color-on-surface-light);
        }
    }
`;

const Description = styled.div`
    word-break: break-word;

    pre, code {
        overflow-wrap: break-all;
        white-space: pre-wrap;
    }
`;

const TagItem = styled.span`
    font-size: 1.4rem;
    color: var(--color-on-surface);
    white-space: nowrap;
    background-color: #F2F2F2;
    padding: .1rem .5rem .2rem;
    border-radius: .3rem;
    margin-top: .3rem;
    margin-right: .5rem;

    &.fixedin {
        background-color: #B9ECCE;
    }
`;

const TagsContainer = styled.span<{ alignLeft?: boolean }>`
    display: flex;
    justify-content: flex-start;
    align-items: center;
    flex-wrap: wrap;
    flex-grow: 1;
    padding: .6rem 0.5rem .9rem 0.5rem;

    justify-content: flex-start;
    margin-left: 0;

    ${TagItem} + ${TagItem} {
        padding: 0 0.8rem;
    }

    button {
        margin-left: 1.5rem;
        font-size: 1.4rem;
    }
`;

const TagsBar = styled.div`
    display: -webkit-box;
    -webkit-box-align: center;
    border-top: solid .2rem #F4F4F4;
    border-bottom: solid .2rem #F4F4F4;
    margin: 2rem;

    ${forMinWidth.lg`
        grid-template-columns: 1fr;
        grid-gap: 2rem;
    `}
`;

const ReportDate = styled.span`
    font-size: 1.4rem;
`;

const Author = styled.a`
    font-size: 1.4rem;
    color: var(--color-on-surface-link);
`;

const AuthorBar = styled.div`
    && {
        display: grid;
        grid-template-columns: max-content;
        grid-gap: .4rem;
        align-items: center;
        margin-bottom: 0.8rem;
        align-items: center;

        .dash-separator {
            display: none;
        }

        ${forMinWidth.md`
            grid-template-columns: repeat(4, max-content);
            grid-gap: 1rem;

            .dash-separator {
                display: block;
            }
        `}
    }

    .avatar-logo {
        margin-right: 1rem;
    }
`;

const NotificationBar = styled(MessageBar)`
    && {
        margin: .2rem 2rem;
        width: auto;
    }
`;

const OperationNotificationWrapper = styled(OperationNotification)`
    margin: .5rem 0;
`;

const IconCircle = styled.span<{ $isSuggestion: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background-color: ${props => props.$isSuggestion ? '#fcf299ff' : '#f5c1b5ff'} !important;
    border-radius: 50%;
    margin-right: 1rem;
    padding: 1rem;
    width: 4rem;
    height: 4rem;
`;